import { useRouter } from "next/router"
import { Grid, Stack, Typography } from "@octopus-energy/coral/atoms"
import { FormikRadio, FormikRadioOption } from "@octopus-energy/coral/formik"
import { ZodFormik } from "@oefr/ui"
import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { FormActions } from "@/components/organisms"
import { graphql, type InputOf } from "@/graphql"
import { useToast } from "@/hooks/useToast"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { dashboardRoutesPaths } from "@/routes/dashboard"
import {
    BooleanField,
    type ConsentValue,
    SMSCommunicationPreference,
} from "../../constants/consents"
import {
    CommunicationPreferencesFormSchema,
    type CommunicationPreferencesFormValues,
    CommunicationPreferencesParserSchema,
    ConsentsDenormalizer,
} from "../../schemas/consentSchema"
import { type ConsentType } from "../../types/consentType"

const UpdateCommsConsentsMutation = graphql(`
    mutation UpdateAccountUserConsents($consents: [ConsentTypeInput]!) {
        updateAccountUserConsents(consents: $consents) {
            accountUserConsents {
                consents {
                    code
                    value
                }
            }
        }
    }
`)

const UpdateCommsPreferencesMutation = graphql(`
    mutation UpdateCommsPreferences($input: UpdateAccountUserCommsPreferencesMutationInput!) {
        updateCommsPreferences(input: $input) {
            isOptedInToSmsMessages
        }
    }
`)

type UserSettingsConsentsFormProps = {
    consents: ConsentType[]
}

export function UserSettingsConsentsForm({ consents }: UserSettingsConsentsFormProps) {
    const t = useTranslations("UserSettingsConsentsForm")
    const router = useRouter()
    const toast = useToast()
    const graphQLClient = useGraphQLClient()

    const updateConsents = useMutation({
        mutationFn: (consents: Array<{ consentTypeCode: string; value: ConsentValue }>) =>
            graphQLClient.request(UpdateCommsConsentsMutation, { consents }),
    })

    const updatePreferences = useMutation({
        mutationFn: (input: InputOf<typeof UpdateCommsPreferencesMutation>) =>
            graphQLClient.request(UpdateCommsPreferencesMutation, { input }),
    })

    const handleSubmit = async (rawValues: CommunicationPreferencesFormValues) => {
        try {
            const values = rawValues
            const smsPreference = rawValues[SMSCommunicationPreference]
            delete values[SMSCommunicationPreference]

            const denormalizedConsents = ConsentsDenormalizer.parse(values)

            await updateConsents.mutateAsync(denormalizedConsents)
            await updatePreferences.mutateAsync({
                [SMSCommunicationPreference]: smsPreference === BooleanField.YES,
            })

            toast("success.update", { type: "success" })
            await router.push(dashboardRoutesPaths.personalDetails.resolve())
        } catch {
            toast("error.update", { type: "error" })
        }
    }

    return (
        <ZodFormik
            initialValues={CommunicationPreferencesParserSchema.parse(consents)}
            validationSchema={CommunicationPreferencesFormSchema}
            onSubmit={handleSubmit}
        >
            <Grid gap={{ base: "xs", sm: "lg", md: "sm" }}>
                {consents &&
                    consents.map((consent) => (
                        <Grid
                            gap="xs"
                            key={consent.id}
                        >
                            <Typography>
                                {t.rich("title", {
                                    consentCode: consent.code,
                                    br: () => <br />,
                                })}
                            </Typography>
                            <FormikRadio name={consent.code}>
                                <Stack>
                                    <FormikRadioOption value="yes">
                                        {t("radio.yes")}
                                    </FormikRadioOption>
                                    <FormikRadioOption value="no">
                                        {t("radio.no")}
                                    </FormikRadioOption>
                                </Stack>
                            </FormikRadio>
                        </Grid>
                    ))}

                <FormActions>
                    <FormActions.Error error={updatePreferences.error} />
                    <FormActions.Accept />
                </FormActions>
            </Grid>
        </ZodFormik>
    )
}

UserSettingsConsentsForm.messages = [
    "UserSettingsConsentsForm",
    ...FormActions.Error.messages,
] as const
